import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Crown, Send, Mic, MicOff, Loader2, Bot, Sparkles, X,
  Zap, Shield, CheckCircle2, AlertTriangle, Clock, Play,
  FileText, Target, Settings, ChevronRight, Plus, Eye,
  Volume2, VolumeX, Trash2, RefreshCw, Brain, Workflow,
  Paperclip, Image as ImageIcon, File, XCircle, Lock,
  MessageSquare, Mail, Phone, Pin, Edit2, ThumbsDown,
  ExternalLink, User, Briefcase, AlertCircle, Calendar, Download,
  FolderPlus, History, ChevronDown, MoreVertical, FolderOpen
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from './ui/dropdown-menu';
import PrimeQuickActions from './PrimeQuickActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Agent Factory Panel for Prime Assistant
const AgentFactoryPanel = ({ proposals, loadingProposals, fetchProposals, onApprove, onReject }) => {
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [AgentFactoryModal, setAgentFactoryModal] = useState(null);

  useEffect(() => {
    fetchAgents();
    // Dynamically import the modal
    import('./AgentFactoryModal').then(module => {
      setAgentFactoryModal(() => module.default);
    });
  }, []);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/agents`);
      setAgents(res.data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'test': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'pending_approval': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const pendingProposals = proposals.filter(p => p.status === 'pending');
  const pendingAgents = agents.filter(a => a.status === 'pending_approval');
  const activeAgents = agents.filter(a => ['live', 'test'].includes(a.status));

  return (
    <div className="space-y-3">
      {/* Create Agent Button */}
      <Button 
        onClick={() => setShowCreateModal(true)}
        className="w-full bg-purple-600 hover:bg-purple-700"
      >
        <Plus className="h-4 w-4 mr-2" />
        Create New Agent
      </Button>

      {/* Pending Approvals */}
      {(pendingProposals.length > 0 || pendingAgents.length > 0) && (
        <>
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-muted-foreground uppercase font-medium flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              Pending Approval ({pendingProposals.length + pendingAgents.length})
            </p>
            <Button variant="ghost" size="sm" onClick={() => { fetchProposals(); fetchAgents(); }}>
              <RefreshCw className={`h-3 w-3 ${loadingProposals ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {pendingProposals.map(proposal => (
            <Card key={proposal.id} className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{proposal.name}</CardTitle>
                  <Badge variant="outline" className="text-xs">Proposal</Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground mb-3">{proposal.purpose}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => onApprove(proposal.id, 'test')}>
                    Test Mode
                  </Button>
                  <Button size="sm" className="flex-1 text-xs bg-green-600 hover:bg-green-700" onClick={() => onApprove(proposal.id, 'live')}>
                    Go Live
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {pendingAgents.map(agent => (
            <Card key={agent.id} className="border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{agent.name}</CardTitle>
                  <Badge className={`text-xs ${getStatusColor(agent.status)}`}>Pending Live</Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground mb-2">{agent.purpose}</p>
                {agent.capabilities && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {agent.capabilities.slice(0, 3).map(cap => (
                      <Badge key={cap} variant="outline" className="text-xs">{cap.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={async () => {
                    try {
                      await axios.post(`${API}/platform-ai/agents/${agent.id}/status`, null, { params: { new_status: 'test' } });
                      toast.success('Agent moved to test mode');
                      fetchAgents();
                    } catch (e) { toast.error('Failed to update'); }
                  }}>
                    Test First
                  </Button>
                  <Button size="sm" className="flex-1 text-xs bg-green-600 hover:bg-green-700" onClick={async () => {
                    try {
                      await axios.post(`${API}/platform-ai/agents/${agent.id}/status`, null, { params: { new_status: 'live' } });
                      toast.success('Agent is now live!');
                      fetchAgents();
                    } catch (e) { toast.error('Failed to update'); }
                  }}>
                    Approve Live
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}

      {pendingProposals.length === 0 && pendingAgents.length === 0 && (
        <div className="text-center py-4 border border-dashed rounded-lg">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500 opacity-50" />
          <p className="text-xs text-muted-foreground">No pending approvals</p>
        </div>
      )}

      <Separator className="my-2" />

      {/* Active Agents */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground uppercase font-medium">Active Agents ({activeAgents.length})</p>
      </div>

      {activeAgents.slice(0, 5).map(agent => (
        <div key={agent.id} className="flex items-center justify-between p-2 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-purple-500" />
            <div>
              <span className="text-sm font-medium">{agent.name}</span>
              <p className="text-xs text-muted-foreground">{agent.total_runs || 0} runs</p>
            </div>
          </div>
          <Badge className={`text-xs ${getStatusColor(agent.status)}`}>
            {agent.status}
          </Badge>
        </div>
      ))}

      {activeAgents.length === 0 && !loadingAgents && (
        <div className="text-center py-4">
          <Workflow className="h-10 w-10 mx-auto mb-2 text-muted-foreground opacity-30" />
          <p className="text-xs text-muted-foreground">No active agents yet</p>
          <p className="text-xs text-muted-foreground">Create your first agent above!</p>
        </div>
      )}

      {/* Agent Factory Modal */}
      {showCreateModal && AgentFactoryModal && (
        <AgentFactoryModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { fetchAgents(); fetchProposals(); }}
        />
      )}
    </div>
  );
};

// Default Prime avatar
const DEFAULT_PRIME_AVATAR = "https://customer-assets.emergentagent.com/job_platform-control-2/artifacts/xft2raj0_PRIME.png";

// Calendar Event Created Card Component
const CalendarEventCard = ({ event, onOpenEvent, onViewCalendar, onEdit, onDelete, type = "success" }) => {
  const isError = type === "error";
  const isInternalOnly = event?.manual_add_required || !event?.external_calendar_synced;
  
  // Function to download ICS file
  const downloadICS = () => {
    if (!event?.ics_content) return;
    const blob = new Blob([event.ics_content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title?.replace(/[^a-z0-9]/gi, '_') || 'event'}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Generate Google Calendar add URL
  const getGoogleCalendarUrl = () => {
    if (!event?.start_datetime || !event?.end_datetime) return null;
    const startDate = new Date(event.start_datetime).toISOString().replace(/-|:|\.\d+/g, '');
    const endDate = new Date(event.end_datetime).toISOString().replace(/-|:|\.\d+/g, '');
    const title = encodeURIComponent(event.title || '');
    const details = encodeURIComponent(event.description || '');
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${details}`;
  };
  
  return (
    <Card className={`my-2 ${
      isError 
        ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10' 
        : isInternalOnly
          ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
          : 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
    }`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            {isError ? (
              <>
                <AlertCircle className="h-4 w-4 text-red-500" />
                Event Not Created
              </>
            ) : isInternalOnly ? (
              <>
                <AlertCircle className="h-4 w-4 text-amber-500" />
                Event Prepared (Manual Add Required)
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Event Added to Calendar
              </>
            )}
          </CardTitle>
          {!isError && (
            <Badge variant="outline" className={`text-xs ${isInternalOnly ? 'text-amber-600' : 'text-green-600'}`}>
              {isInternalOnly ? 'INTERNAL ONLY' : 'SYNCED'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isError ? (
          <>
            <p className="text-sm text-red-600">{event.error || 'Failed to create event'}</p>
            <p className="text-xs text-muted-foreground">{event.error_reason}</p>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => onEdit?.(event)}>
                <Edit2 className="h-4 w-4 mr-1" />
                Edit & Retry
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="font-medium">{event.title}</p>
              <p className="text-sm text-muted-foreground">
                {new Date(event.start_datetime).toLocaleString()} - {new Date(event.end_datetime).toLocaleTimeString()}
              </p>
              {event.timezone && <p className="text-xs text-muted-foreground">{event.timezone}</p>}
            </div>
            
            {/* Status Message */}
            {event.status_message && (
              <p className={`text-xs ${isInternalOnly ? 'text-amber-600' : 'text-green-600'}`}>
                {event.status_message}
              </p>
            )}
            
            {event.target_user_name && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                Calendar: {event.target_user_name}
              </div>
            )}
            
            {(event.related_job_id || event.related_customer_id) && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Briefcase className="h-3 w-3" />
                Related: {event.related_job_id ? `Job` : 'Contact'}
              </div>
            )}
            
            {/* Manual Add Options (when external calendar not connected) */}
            {isInternalOnly && (
              <div className="bg-amber-100/50 dark:bg-amber-900/20 rounded-md p-2 mt-2">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2">
                  ⚠️ Add to your external calendar:
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => window.open(getGoogleCalendarUrl(), '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Add to Google
                  </Button>
                  {event.ics_content && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-xs h-7"
                      onClick={downloadICS}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download .ICS
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => window.open(`/calendar?event=${event.event_id}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                View in Rhino-1
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => window.open('/calendar', '_blank')}
              >
                <Clock className="h-4 w-4 mr-1" />
                Calendar
              </Button>
              {onDelete && (
                <Button variant="ghost" size="sm" className="text-red-500" onClick={() => onDelete(event.event_id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Goal Created Card Component (for Prime Goals)
const GoalCreatedCard = ({ goal, onViewGoal, onPause, onClose }) => {
  const getStatusColor = (status) => {
    const colors = {
      'ACTIVE': 'bg-blue-100 text-blue-800 border-blue-200',
      'WAITING_CUSTOMER': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'WAITING_SUPER_ADMIN': 'bg-orange-100 text-orange-800 border-orange-200',
      'COMPLETED': 'bg-green-100 text-green-800 border-green-200',
      'ESCALATED': 'bg-red-100 text-red-800 border-red-200',
      'PAUSED': 'bg-gray-100 text-gray-800 border-gray-200',
      'CANCELLED': 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Card className="border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 my-2">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4 text-purple-500" />
            Goal Created
          </CardTitle>
          <Badge className={getStatusColor(goal.status)}>
            {goal.status?.replace('_', ' ')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <div className="text-sm font-medium">{goal.goal_title}</div>
        <div className="text-xs text-muted-foreground space-y-1">
          <div className="flex items-center gap-2">
            <User className="h-3 w-3" />
            <span>{goal.recipient_name}</span>
            {goal.recipient_phone && (
              <span className="text-muted-foreground">• {goal.recipient_phone}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3 w-3" />
            <span className="truncate">{goal.success_criteria}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-3 w-3" />
            <span>Attempt {goal.current_attempt || 1} of {goal.max_attempts || 3}</span>
          </div>
        </div>
        
        {goal.initial_message && (
          <div className="p-2 bg-white dark:bg-gray-800 rounded text-xs border mt-2">
            <div className="text-muted-foreground mb-1">Initial Message Sent:</div>
            <span>&quot;{goal.initial_message}&quot;</span>
          </div>
        )}
        
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => onViewGoal?.(goal)}>
            <Eye className="h-3 w-3 mr-1" />
            View Thread
          </Button>
          {goal.status !== 'COMPLETED' && goal.status !== 'CANCELLED' && (
            <Button size="sm" variant="outline" onClick={() => onPause?.(goal)}>
              <Clock className="h-3 w-3 mr-1" />
              {goal.status === 'PAUSED' ? 'Resume' : 'Pause'}
            </Button>
          )}
          {goal.status === 'WAITING_CUSTOMER' && (
            <Button size="sm" variant="outline" className="text-green-600" onClick={() => onClose?.(goal)}>
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Mark Complete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Message Preview Card Component (for Send Now SMS/Email)
const MessagePreviewCard = ({ draft, onSend, onEdit, onCancel, sending, user }) => {
  const [editing, setEditing] = useState(false);
  const [editedBody, setEditedBody] = useState(draft.message_body);
  const [editedSubject, setEditedSubject] = useState(draft.subject || '');
  const [editedEmail, setEditedEmail] = useState(draft.recipient_email || '');
  const [editedPhone, setEditedPhone] = useState(draft.recipient_phone || '');
  const [overrideQuiet, setOverrideQuiet] = useState(false);
  const [quietHoursWarning, setQuietHoursWarning] = useState(null);
  
  // Quick Actions state
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpDays, setFollowUpDays] = useState(3);
  const [followUpNote, setFollowUpNote] = useState('');
  const [linkToJob, setLinkToJob] = useState(draft.related_job_id || '');
  const [linkToCustomer, setLinkToCustomer] = useState(draft.related_customer_id || '');
  const [showLinkOptions, setShowLinkOptions] = useState(false);
  const [saveContact, setSaveContact] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  
  useEffect(() => {
    // Check quiet hours on mount
    axios.get(`${API}/prime/communications/check-quiet-hours`).then(res => {
      if (res.data.is_quiet_hours) {
        setQuietHoursWarning(res.data.message);
      }
    }).catch(() => {});
  }, []);
  
  // Fetch jobs and customers when link options are shown
  useEffect(() => {
    if (showLinkOptions && jobs.length === 0) {
      const fetchLinks = async () => {
        const [jobsRes, customersRes] = await Promise.all([
          axios.get(`${API}/jobs?limit=20`).catch(() => ({ data: { jobs: [] } })),
          axios.get(`${API}/customers?limit=20`).catch(() => ({ data: { customers: [] } }))
        ]);
        setJobs(jobsRes.data.jobs || []);
        setCustomers(customersRes.data.customers || customersRes.data || []);
        setLoadingLinks(false);
      };
      fetchLinks();
    }
  }, [showLinkOptions, jobs.length]);
  
  const handleSend = () => {
    if (quietHoursWarning && !overrideQuiet) {
      toast.error('Please confirm quiet hours override to send');
      return;
    }
    
    // For email, validate email is provided
    const isEmail = draft.channel === 'email';
    if (isEmail && !editedEmail && !draft.recipient_email) {
      toast.error('Please enter an email address');
      return;
    }
    
    // For SMS, validate phone is provided
    const isSMS = draft.channel === 'sms';
    if (isSMS && !editedPhone && !draft.recipient_phone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    onSend({
      ...draft,
      message_body: editedBody,
      subject: editedSubject,
      recipient_email: editedEmail || draft.recipient_email,
      recipient_phone: editedPhone || draft.recipient_phone,
      override_quiet_hours: overrideQuiet,
      approved_by_user_id: user?.id,
      approved_via: "PRIME_CHAT_SEND_NOW",
      // Quick Actions data
      create_follow_up: createFollowUp,
      follow_up_days: followUpDays,
      follow_up_note: followUpNote,
      related_job_id: linkToJob || draft.related_job_id,
      related_customer_id: linkToCustomer || draft.related_customer_id,
      save_contact: saveContact
    });
  };
  
  const isSMS = draft.channel === 'sms';
  const Icon = isSMS ? Phone : Mail;
  const needsRecipient = isSMS ? draft.needs_phone_number : draft.needs_email_address;
  
  return (
    <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 my-2">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Icon className={`h-4 w-4 ${isSMS ? 'text-green-500' : 'text-blue-500'}`} />
            {isSMS ? 'SMS Draft' : 'Email Draft'}
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {draft.channel.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Recipient */}
        <div className="flex items-center gap-2 text-sm">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{draft.recipient_name}</span>
          {needsRecipient ? (
            <Input
              placeholder={isSMS ? "Enter phone number..." : "Enter email..."}
              value={isSMS ? editedPhone : editedEmail}
              onChange={(e) => isSMS ? setEditedPhone(e.target.value) : setEditedEmail(e.target.value)}
              className="h-7 text-sm flex-1"
              type={isSMS ? "tel" : "email"}
            />
          ) : (
            <span className="text-muted-foreground">
              {isSMS ? draft.recipient_phone : draft.recipient_email}
            </span>
          )}
        </div>
        
        {/* Job/Contact Link */}
        {(draft.related_job_id || draft.related_customer_id) && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Briefcase className="h-3 w-3" />
            <span>Related: {draft.related_job_id ? `Job #${draft.related_job_id.slice(0,8)}` : `Customer`}</span>
            <ExternalLink className="h-3 w-3" />
          </div>
        )}
        
        {/* Subject (email only) */}
        {!isSMS && (
          <div>
            <label className="text-xs text-muted-foreground">Subject</label>
            {editing ? (
              <Input
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
                className="mt-1 text-sm"
              />
            ) : (
              <p className="text-sm font-medium">{editedSubject || 'No subject'}</p>
            )}
          </div>
        )}
        
        {/* Message Body */}
        <div>
          <label className="text-xs text-muted-foreground">Message</label>
          {editing ? (
            <Textarea
              value={editedBody}
              onChange={(e) => setEditedBody(e.target.value)}
              className="mt-1 text-sm min-h-[100px]"
            />
          ) : (
            <p className="text-sm bg-white dark:bg-slate-800 p-2 rounded border mt-1 whitespace-pre-wrap">
              {editedBody}
            </p>
          )}
        </div>
        
        {/* Quiet Hours Warning */}
        {quietHoursWarning && (
          <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
            <span className="text-xs text-amber-700 dark:text-amber-400">{quietHoursWarning}</span>
            <div className="flex items-center gap-1 ml-auto">
              <span className="text-xs">Override</span>
              <Switch checked={overrideQuiet} onCheckedChange={setOverrideQuiet} />
            </div>
          </div>
        )}
        
        {/* Quick Actions Section */}
        <div className="border-t pt-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Zap className="h-3 w-3" /> Quick Actions
            </span>
          </div>
          
          {/* Create Follow-up Task */}
          <div className="flex items-start gap-2">
            <div className="flex items-center gap-2 flex-1">
              <input
                type="checkbox"
                id="createFollowUp"
                checked={createFollowUp}
                onChange={(e) => setCreateFollowUp(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="createFollowUp" className="text-xs cursor-pointer flex items-center gap-1">
                <Target className="h-3 w-3" />
                Create follow-up task
              </label>
            </div>
            {createFollowUp && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">in</span>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={followUpDays}
                  onChange={(e) => setFollowUpDays(parseInt(e.target.value) || 3)}
                  className="h-6 w-14 text-xs text-center"
                />
                <span className="text-xs text-muted-foreground">days</span>
              </div>
            )}
          </div>
          
          {/* Follow-up Note (shown when follow-up is enabled) */}
          {createFollowUp && (
            <Input
              placeholder="Follow-up note (optional)..."
              value={followUpNote}
              onChange={(e) => setFollowUpNote(e.target.value)}
              className="h-7 text-xs"
            />
          )}
          
          {/* Link to Job/Customer */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1">
              <input
                type="checkbox"
                id="linkOptions"
                checked={showLinkOptions}
                onChange={(e) => setShowLinkOptions(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="linkOptions" className="text-xs cursor-pointer flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                Link to job/customer
              </label>
            </div>
          </div>
          
          {/* Job/Customer Selectors (shown when enabled) */}
          {showLinkOptions && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Job</label>
                <select
                  value={linkToJob}
                  onChange={(e) => setLinkToJob(e.target.value)}
                  className="w-full h-7 text-xs rounded border border-input bg-background px-2"
                  disabled={loadingLinks}
                >
                  <option value="">Select job...</option>
                  {jobs.map(job => (
                    <option key={job.id} value={job.id}>
                      {job.job_name || job.address || `Job #${job.id.slice(0,6)}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Customer</label>
                <select
                  value={linkToCustomer}
                  onChange={(e) => setLinkToCustomer(e.target.value)}
                  className="w-full h-7 text-xs rounded border border-input bg-background px-2"
                  disabled={loadingLinks}
                >
                  <option value="">Select customer...</option>
                  {customers.map(cust => (
                    <option key={cust.id} value={cust.id}>
                      {cust.name || `${cust.first_name} ${cust.last_name}`.trim() || `Customer #${cust.id.slice(0,6)}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          
          {/* Save as Contact (for new recipients) */}
          {needsRecipient && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="saveContact"
                checked={saveContact}
                onChange={(e) => setSaveContact(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="saveContact" className="text-xs cursor-pointer flex items-center gap-1">
                <User className="h-3 w-3" />
                Save as Prime contact
              </label>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            onClick={handleSend}
            disabled={sending || (quietHoursWarning && !overrideQuiet)}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            Send Now
          </Button>
          <Button variant="outline" size="icon" onClick={() => setEditing(!editing)}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Memory Action Buttons for messages
const MessageActions = ({ message, onSaveMemory, onPinMemory, onMarkIncorrect }) => {
  const [showActions, setShowActions] = useState(false);
  
  if (message.role !== 'assistant') return null;
  
  return (
    <div className="relative inline-block">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
        onClick={() => setShowActions(!showActions)}
      >
        <Brain className="h-3 w-3" />
      </Button>
      
      {showActions && (
        <div className="absolute right-0 top-6 z-10 bg-white dark:bg-slate-800 border rounded-lg shadow-lg p-1 flex flex-col gap-1 min-w-[140px]">
          <Button
            variant="ghost"
            size="sm"
            className="justify-start text-xs h-7"
            onClick={() => { onSaveMemory(message.content); setShowActions(false); }}
          >
            <Brain className="h-3 w-3 mr-2" />
            Save as Memory
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="justify-start text-xs h-7"
            onClick={() => { onPinMemory(message.content); setShowActions(false); }}
          >
            <Pin className="h-3 w-3 mr-2" />
            Pin Memory
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="justify-start text-xs h-7 text-red-600"
            onClick={() => { onMarkIncorrect(message.content); setShowActions(false); }}
          >
            <ThumbsDown className="h-3 w-3 mr-2" />
            Mark Incorrect
          </Button>
        </div>
      )}
    </div>
  );
};

// Prime Assistant Component (Full Screen for Super Admin)
const PrimeAssistant = ({ open, onClose, user }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Good ${getGreeting()}, Ryan. 🔱

**Chief-of-Staff Mode Active.**

I'm here to protect your time, momentum, and focus—not fill your calendar.

What's the priority right now?`,
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  // Prime profile & avatar state
  const [primeProfile, setPrimeProfile] = useState(null);
  const [primeAvatar, setPrimeAvatar] = useState(DEFAULT_PRIME_AVATAR);
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  
  // Agent proposals
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  
  // Delegations state
  const [delegations, setDelegations] = useState([]);
  const [delegationDashboard, setDelegationDashboard] = useState(null);
  const [loadingDelegations, setLoadingDelegations] = useState(false);
  
  // Message drafts for Send Now
  const [pendingDrafts, setPendingDrafts] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Calendar events created by Prime
  const [calendarEvents, setCalendarEvents] = useState([]);
  
  // Prime Goals
  const [activeGoals, setActiveGoals] = useState([]);
  
  // Unread Messages State
  const [unreadMessages, setUnreadMessages] = useState(null);
  const [unreadDismissed, setUnreadDismissed] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);  // { channel_id, channel_name, sender_name }
  
  // Chat Projects State (like ChatGPT)
  const [chatProjects, setChatProjects] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isNewChatMode, setIsNewChatMode] = useState(false); // Flag to skip loading history;
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [loadingChats, setLoadingChats] = useState(false);
  
  // Fetch chat projects and history
  const fetchChatProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/prime/chat-projects`);
      setChatProjects(res.data.projects || []);
    } catch (error) {
      console.error('Error fetching chat projects:', error);
    }
  }, []);
  
  const fetchChatHistory = useCallback(async (projectId = null) => {
    try {
      setLoadingChats(true);
      const url = projectId 
        ? `${API}/prime/chats?project_id=${projectId}`
        : `${API}/prime/chats`;
      const res = await axios.get(url);
      setChatHistory(res.data.chats || []);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setLoadingChats(false);
    }
  }, []);
  
  // Load a specific chat from history
  const loadChat = useCallback(async (chatId) => {
    try {
      const res = await axios.get(`${API}/prime/chats/${chatId}`);
      setMessages(res.data.messages || []);
      setCurrentChatId(chatId);
      setSessionId(res.data.session_id || null);
    } catch (error) {
      console.error('Error loading chat:', error);
      toast.error('Failed to load chat');
    }
  }, []);
  
  // Save current chat to history
  const saveCurrentChat = useCallback(async () => {
    if (messages.length <= 1) return null; // Don't save empty chats
    
    try {
      if (currentChatId) {
        // Update existing chat
        await axios.put(`${API}/prime/chats/${currentChatId}`, {
          messages: messages,
          project_id: selectedProjectId
        });
        return currentChatId;
      } else {
        // Create new chat
        const res = await axios.post(`${API}/prime/chats`, {
          messages: messages,
          project_id: selectedProjectId
        });
        setCurrentChatId(res.data.id);
        return res.data.id;
      }
    } catch (error) {
      console.error('Error saving chat:', error);
      return null;
    }
  }, [messages, currentChatId, selectedProjectId]);
  
  // Create new project
  const createProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const res = await axios.post(`${API}/prime/chat-projects`, {
        name: newProjectName.trim()
      });
      setChatProjects(prev => [res.data, ...prev]);
      setSelectedProjectId(res.data.id);
      setNewProjectName('');
      setShowNewProjectDialog(false);
      toast.success('Project created');
    } catch (error) {
      toast.error('Failed to create project');
    }
  };
  
  // Delete project
  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/prime/chat-projects/${projectId}`);
      setChatProjects(prev => prev.filter(p => p.id !== projectId));
      if (selectedProjectId === projectId) {
        setSelectedProjectId(null);
      }
      fetchChatHistory();
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };
  
  // Delete chat from history
  const deleteChat = async (chatId) => {
    try {
      await axios.delete(`${API}/prime/chats/${chatId}`);
      setChatHistory(prev => prev.filter(c => c.id !== chatId));
      if (currentChatId === chatId) {
        startNewChat();
      }
      toast.success('Chat deleted');
    } catch (error) {
      toast.error('Failed to delete chat');
    }
  };
  
  // Start a new chat (clears current and saves if needed)
  const startNewChat = useCallback(async () => {
    // Save current chat first if it has content
    if (messages.length > 1) {
      await saveCurrentChat();
    }
    
    // Set flag to prevent loading old context
    setIsNewChatMode(true);
    
    // Reset to initial state with fresh greeting
    const newSessionId = `prime_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    setCurrentChatId(null);
    setMessages([{
      role: 'assistant',
      content: `Good ${getGreeting()}, ${user?.first_name || 'there'}! 🔱\n\n**Fresh conversation started.**\n\nHow can I help you?`,
      timestamp: new Date().toISOString()
    }]);
    
    // Refresh chat history
    fetchChatHistory(selectedProjectId);
    
    // Reset the flag after a short delay
    setTimeout(() => setIsNewChatMode(false), 500);
  }, [messages, saveCurrentChat, fetchChatHistory, selectedProjectId, user?.first_name]);
  
  // Fetch projects and history on mount
  useEffect(() => {
    if (open) {
      fetchChatProjects();
      fetchChatHistory();
    }
  }, [open, fetchChatProjects, fetchChatHistory]);
  
  // Auto-save chat when messages change (debounced)
  useEffect(() => {
    if (messages.length > 1 && currentChatId) {
      const timer = setTimeout(() => {
        saveCurrentChat();
      }, 2000); // Save after 2 seconds of no new messages
      return () => clearTimeout(timer);
    }
  }, [messages, currentChatId, saveCurrentChat]);
  
  // Fetch unread messages when Prime opens
  useEffect(() => {
    const fetchUnreadMessages = async () => {
      if (!user?.id || !open) return;
      try {
        const res = await axios.get(`${API}/prime/unread-messages/${user.id}`);
        setUnreadMessages(res.data);
        setUnreadDismissed(false);  // Reset dismissed state when reopening
        
        // If there are urgent messages, show them immediately
        if (res.data.has_urgent && res.data.urgent?.length > 0) {
          const urgentMsg = res.data.urgent[0];
          setMessages(prev => {
            // Don't add if already notified
            if (prev.some(m => m.urgentNotification)) return prev;
            return [...prev, {
              role: 'assistant',
              content: `🚨 **Urgent Message** from ${urgentMsg.sender_name} (${urgentMsg.sender_role}):\n\n"${urgentMsg.content}"\n\n_Say "reply to ${urgentMsg.sender_name}" to respond, or "skip" to dismiss._`,
              timestamp: new Date().toISOString(),
              urgentNotification: true,
              urgentData: urgentMsg
            }];
          });
        }
      } catch (error) {
        console.error('Error fetching unread messages:', error);
      }
    };
    
    if (open) {
      fetchUnreadMessages();
    }
  }, [open, user?.id]);
  
  // Handle acknowledging/dismissing messages
  const handleAcknowledgeMessages = async (channelIds = null, acknowledgeAll = false) => {
    if (!user?.id) return;
    try {
      await axios.post(`${API}/prime/acknowledge-messages`, {
        user_id: user.id,
        channel_ids: channelIds,
        acknowledge_all: acknowledgeAll
      });
      setUnreadMessages(null);
      setUnreadDismissed(true);
      toast.success('Messages acknowledged');
    } catch (error) {
      console.error('Error acknowledging messages:', error);
    }
  };
  
  // Handle replying via Prime
  const handleReplyViaPrime = async (channelId, message, channelName) => {
    if (!user?.id) return;
    try {
      const res = await axios.post(`${API}/prime/reply-via-prime`, {
        channel_id: channelId,
        message: message,
        sender_user_id: user.id
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setReplyingTo(null);
        
        // Acknowledge this channel
        await axios.post(`${API}/prime/acknowledge-messages`, {
          user_id: user.id,
          channel_ids: [channelId]
        });
        
        // Refresh unread count
        const unreadRes = await axios.get(`${API}/prime/unread-messages/${user.id}`);
        setUnreadMessages(unreadRes.data);
        
        return res.data;
      }
    } catch (error) {
      toast.error('Failed to send reply');
      console.error('Error replying via Prime:', error);
    }
  };
  
  // Fetch active goals on mount
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const res = await axios.get(`${API}/prime/goals/list?status=WAITING_CUSTOMER`);
        setActiveGoals(res.data.goals || []);
      } catch (error) {
        console.error('Error fetching goals:', error);
      }
    };
    if (open) fetchGoals();
  }, [open]);
  
  // Handler to create a Prime goal
  const handleCreateGoal = async (goalData) => {
    try {
      const response = await axios.post(`${API}/prime/goals/create`, goalData);
      
      if (response.data.success) {
        toast.success('🎯 Goal created and message sent!');
        setActiveGoals(prev => [...prev, response.data.goal]);
        return response.data;
      } else {
        toast.error(response.data.error || 'Failed to create goal');
        return response.data;
      }
    } catch (error) {
      toast.error('Failed to create goal');
      return { success: false, error: error.message };
    }
  };
  
  // Handler to view goal thread
  const handleViewGoal = (goal) => {
    // Could open a modal or navigate to communications
    toast.info(`Goal: ${goal.goal_title}\nThread ID: ${goal.thread_id}`);
  };
  
  // Handler to pause/resume goal
  const handlePauseGoal = async (goal) => {
    try {
      const endpoint = goal.status === 'PAUSED' ? 'resume' : 'pause';
      await axios.post(`${API}/prime/goals/${goal.id}/${endpoint}`);
      toast.success(goal.status === 'PAUSED' ? 'Goal resumed' : 'Goal paused');
      // Refresh goals
      const res = await axios.get(`${API}/prime/goals/list`);
      setActiveGoals(res.data.goals?.filter(g => !['COMPLETED', 'CANCELLED'].includes(g.status)) || []);
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };
  
  // Handler to complete goal
  const handleCompleteGoal = async (goal) => {
    try {
      await axios.post(`${API}/prime/goals/${goal.id}/complete`);
      toast.success('Goal marked complete!');
      setActiveGoals(prev => prev.filter(g => g.id !== goal.id));
    } catch (error) {
      toast.error('Failed to complete goal');
    }
  };
  
  // Handler to create calendar event from Prime
  const handleCreateCalendarEvent = async (eventData) => {
    try {
      const response = await axios.post(`${API}/prime/calendar/create`, {
        ...eventData,
        created_by_user_id: user?.id,
        created_by_ai: "prime"
      });
      
      if (response.data.success) {
        toast.success('Calendar event created!');
        setCalendarEvents(prev => [...prev, { ...response.data, type: 'success' }]);
        return response.data;
      } else {
        toast.error(response.data.error || 'Failed to create event');
        setCalendarEvents(prev => [...prev, { ...response.data, type: 'error' }]);
        return response.data;
      }
    } catch (error) {
      toast.error('Failed to create calendar event');
      const errorData = { success: false, error: error.message, type: 'error' };
      setCalendarEvents(prev => [...prev, errorData]);
      return errorData;
    }
  };
  
  // Delete calendar event
  const handleDeleteCalendarEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/prime/calendar/events/${eventId}?deleted_by_user_id=${user?.id}`);
      setCalendarEvents(prev => prev.filter(e => e.event_id !== eventId));
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };
  
  // Parse Prime response for calendar event creation intent
  const parseCalendarIntent = (response, userMessage) => {
    const lowerResponse = response.toLowerCase();
    const lowerMessage = userMessage.toLowerCase();
    
    // Check if Prime is claiming to create an event
    if ((lowerMessage.includes('event') || lowerMessage.includes('meeting') || lowerMessage.includes('schedule') || lowerMessage.includes('calendar')) &&
        (lowerResponse.includes('created') || lowerResponse.includes('scheduled') || lowerResponse.includes('added to') || lowerResponse.includes('i\'ve scheduled'))) {
      
      // Extract event details from the response
      const titleMatch = response.match(/["']([^"']+)["']/);
      const title = titleMatch ? titleMatch[1] : null;
      
      // Try to find date/time
      const dateMatch = response.match(/(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{1,2}:\d{2}\s*(?:am|pm)?|tomorrow|today)/i);
      
      if (title) {
        return {
          detected: true,
          title: title,
          raw_response: response
        };
      }
    }
    return { detected: false };
  };
  
  // Memory action handlers
  const handleSaveMemory = async (content) => {
    try {
      await axios.post(`${API}/prime/memory/force-remember`, {
        content: content.slice(0, 500),
        item_type: "fact",
        is_pinned: false,
        source_conversation_id: sessionId
      });
      toast.success('Memory saved!');
    } catch (error) {
      toast.error('Failed to save memory');
    }
  };
  
  const handlePinMemory = async (content) => {
    try {
      await axios.post(`${API}/prime/memory/force-remember`, {
        content: content.slice(0, 500),
        item_type: "fact",
        is_pinned: true,
        source_conversation_id: sessionId
      });
      toast.success('Memory pinned!');
    } catch (error) {
      toast.error('Failed to pin memory');
    }
  };
  
  const handleMarkIncorrect = async (content) => {
    // For now, just show a toast - full implementation would need the memory item ID
    toast.info('Please use Platform AI → Prime → Memory to manage memories');
  };
  
  // Send Now handler for SMS/Email drafts
  const handleSendNow = async (draft) => {
    setSendingMessage(true);
    try {
      let response;
      let confirmationDetails = [];
      
      if (draft.channel === 'email') {
        // Use the new Prime email API (goes through Actions API)
        response = await axios.post(`${API}/prime/send-email`, {
          to: [draft.recipient_email],
          subject: draft.subject || 'Message from Rhino Shield',
          body: draft.message_body,
          recipient_name: draft.recipient_name,
          approved_by_user_id: user?.id,
          save_contact: draft.save_contact || draft.needs_email_address,
          related_job_id: draft.related_job_id,
          related_customer_id: draft.related_customer_id
        });
        
        // Handle Quick Actions - Create follow-up task
        if (draft.create_follow_up && response.data.success) {
          try {
            const followUpDate = new Date();
            followUpDate.setDate(followUpDate.getDate() + (draft.follow_up_days || 3));
            
            await axios.post(`${API}/tasks`, {
              title: `Follow up: ${draft.subject || 'Email to ' + draft.recipient_name}`,
              description: draft.follow_up_note || `Follow up on email sent to ${draft.recipient_name}`,
              due_date: followUpDate.toISOString(),
              status: 'pending',
              priority: 'medium',
              assigned_to: user?.id,
              related_job_id: draft.related_job_id,
              related_customer_id: draft.related_customer_id,
              created_by_ai: 'prime'
            });
            confirmationDetails.push(`📋 Follow-up task created for ${followUpDate.toLocaleDateString()}`);
          } catch (taskError) {
            console.error('Failed to create follow-up task:', taskError);
            toast.error('Email sent but failed to create follow-up task');
          }
        }
        
        if (response.data.requires_approval) {
          toast.info('📧 Email requires approval. Check Action Approvals to send.');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `📧 Email to ${draft.recipient_name} is pending approval.\n\nSubject: "${draft.subject}"\n\nGo to Settings → AI Action Approvals to review and send.`,
            timestamp: new Date().toISOString()
          }]);
        } else if (response.data.success) {
          let confirmMsg = `✅ Email sent to ${draft.recipient_name}!\n\nSubject: "${draft.subject}"`;
          if (confirmationDetails.length > 0) {
            confirmMsg += '\n\n' + confirmationDetails.join('\n');
          }
          if (draft.related_job_id) confirmMsg += '\n🔗 Linked to job';
          if (draft.save_contact) confirmMsg += '\n👤 Contact saved';
          
          toast.success('📧 Email sent successfully!');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: confirmMsg,
            timestamp: new Date().toISOString()
          }]);
        } else {
          toast.error(response.data.error || 'Failed to send email');
        }
      } else if (draft.channel === 'sms') {
        // Check if phone number is available
        if (!draft.recipient_phone) {
          toast.error('Please enter a phone number');
          setSendingMessage(false);
          return;
        }
        
        // Use the new Prime SMS API (goes through Actions API)
        response = await axios.post(`${API}/prime/send-sms`, {
          to: draft.recipient_phone,
          message: draft.message_body,
          recipient_name: draft.recipient_name,
          approved_by_user_id: user?.id,
          save_contact: draft.save_contact || draft.needs_phone_number,
          related_job_id: draft.related_job_id,
          related_customer_id: draft.related_customer_id
        });
        
        // Handle Quick Actions - Create follow-up task
        if (draft.create_follow_up && response.data.success) {
          try {
            const followUpDate = new Date();
            followUpDate.setDate(followUpDate.getDate() + (draft.follow_up_days || 3));
            
            await axios.post(`${API}/tasks`, {
              title: `Follow up: SMS to ${draft.recipient_name}`,
              description: draft.follow_up_note || `Follow up on SMS sent to ${draft.recipient_name}`,
              due_date: followUpDate.toISOString(),
              status: 'pending',
              priority: 'medium',
              assigned_to: user?.id,
              related_job_id: draft.related_job_id,
              related_customer_id: draft.related_customer_id,
              created_by_ai: 'prime'
            });
            confirmationDetails.push(`📋 Follow-up task created for ${followUpDate.toLocaleDateString()}`);
          } catch (taskError) {
            console.error('Failed to create follow-up task:', taskError);
            toast.error('SMS sent but failed to create follow-up task');
          }
        }
        
        if (response.data.requires_approval) {
          toast.info('📱 SMS requires approval. Check Action Approvals to send.');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `📱 SMS to ${draft.recipient_name} is pending approval.\n\nMessage: "${draft.message_body.slice(0, 100)}..."\n\nGo to Settings → AI Action Approvals to review and send.`,
            timestamp: new Date().toISOString()
          }]);
        } else if (response.data.success) {
          let confirmMsg = `✅ SMS sent to ${draft.recipient_name}!\n\nMessage: "${draft.message_body.slice(0, 100)}..."`;
          if (confirmationDetails.length > 0) {
            confirmMsg += '\n\n' + confirmationDetails.join('\n');
          }
          if (draft.related_job_id) confirmMsg += '\n🔗 Linked to job';
          if (draft.save_contact) confirmMsg += '\n👤 Contact saved';
          
          toast.success('📱 SMS sent successfully!');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: confirmMsg,
            timestamp: new Date().toISOString()
          }]);
        } else {
          toast.error(response.data.error || 'Failed to send SMS');
        }
      } else if (draft.channel === 'dm') {
        // Send direct message via Prime API (on behalf of current user)
        response = await axios.post(`${API}/prime/send-dm`, {
          recipient_id: draft.recipient_id,
          message: draft.message_body,
          recipient_name: draft.recipient_name,
          approved_by_user_id: user?.id,
          sender_user_id: user?.id  // Send on behalf of current user
        });
        
        if (response.data.success) {
          toast.success(`💬 DM sent to ${draft.recipient_name}!`);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ Direct message sent to ${draft.recipient_name}!\n\nYou can view this conversation in Chat → Direct Messages.`,
            timestamp: new Date().toISOString()
          }]);
        } else {
          toast.error(response.data.error || 'Failed to send DM');
        }
      } else {
        // Fallback to legacy API
        response = await axios.post(`${API}/prime/communications/send`, draft);
        if (response.data.success) {
          toast.success(`${draft.channel.toUpperCase()} sent successfully!`);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ ${draft.channel.toUpperCase()} sent to ${draft.recipient_name}!\n\nMessage: "${draft.message_body.slice(0, 100)}..."`,
            timestamp: new Date().toISOString()
          }]);
        } else {
          toast.error(response.data.error || 'Failed to send');
        }
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };
  
  // Parse Prime response for drafts
  const parseResponseForDrafts = (response, originalMessage) => {
    // Check if the response contains draft indicators
    const lowerResponse = response.toLowerCase();
    const lowerMessage = originalMessage.toLowerCase();
    
    if ((lowerMessage.includes('text') || lowerMessage.includes('sms') || lowerMessage.includes('message')) &&
        (lowerResponse.includes('draft') || lowerResponse.includes('here\'s') || lowerResponse.includes('message:'))) {
      // Try to extract recipient and message
      // This is a simple heuristic - could be improved
      const lines = response.split('\n');
      let messageBody = '';
      let inQuote = false;
      
      for (const line of lines) {
        if (line.includes('"') || line.includes('"') || line.includes('"')) {
          inQuote = !inQuote;
          const quotedMatch = line.match(/[""](.+)[""]/);
          if (quotedMatch) {
            messageBody = quotedMatch[1];
            break;
          }
        }
        if (inQuote) {
          messageBody += line + ' ';
        }
      }
      
      // Extract recipient name from original message
      const nameMatch = originalMessage.match(/(?:text|message|sms|email)\s+(\w+(?:\s+\w+)?)/i);
      const recipientName = nameMatch ? nameMatch[1] : 'Unknown';
      
      if (messageBody || response.includes('---')) {
        // Extract message between --- markers if present
        const dashMatch = response.match(/---\s*([\s\S]*?)\s*---/);
        if (dashMatch) {
          messageBody = dashMatch[1].trim();
        }
      }
      
      if (messageBody) {
        return {
          id: Date.now().toString(),
          channel: lowerMessage.includes('email') ? 'email' : 'sms',
          recipient_name: recipientName,
          recipient_phone: null, // Would need lookup
          recipient_email: null,
          message_body: messageBody,
          subject: lowerMessage.includes('email') ? 'Message from Rhino Shield' : null
        };
      }
    }
    return null;
  };
  
  // Fetch Prime profile and avatar on open
  useEffect(() => {
    if (open) {
      fetchPrimeProfile();
    }
  }, [open]);
  
  const fetchPrimeProfile = async () => {
    try {
      const res = await axios.get(`${API}/prime/profile`);
      setPrimeProfile(res.data);
      if (res.data.avatar_url) {
        setPrimeAvatar(res.data.avatar_url);
      }
    } catch (error) {
      console.log('Failed to fetch Prime profile:', error);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize session and fetch proposals
  useEffect(() => {
    if (open && !sessionId && !isNewChatMode) {
      // Create a new session for this conversation
      const newSessionId = `prime_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      
      // Fetch recent conversation context for continuity
      const loadRecentContext = async () => {
        try {
          const contextRes = await axios.get(`${API}/prime/conversations/recent-context`, {
            params: { user_id: user?.id || 'unknown', hours: 24, max_messages: 15 }
          });
          
          if (contextRes.data.messages && contextRes.data.messages.length > 0) {
            // Load recent messages for context display
            const recentMsgs = contextRes.data.messages.map(msg => ({
              role: msg.role,
              content: msg.content,
              timestamp: msg.timestamp,
              isHistorical: true // Mark as from previous session
            }));
            
            // Add a separator message if there's history
            if (recentMsgs.length > 0) {
              setMessages([
                ...recentMsgs,
                {
                  role: 'system',
                  content: '— New session started —',
                  timestamp: new Date().toISOString(),
                  isSessionMarker: true
                }
              ]);
            }
          }
        } catch (err) {
          console.log('Could not load recent context:', err);
        }
      };
      
      loadRecentContext();
      
      // Create conversation in backend
      axios.post(`${API}/prime/conversations`, null, {
        params: {
          session_id: newSessionId,
          user_id: user?.id || 'unknown',
          user_email: user?.email || 'rmarch@rhinoshieldflorida.com',
          user_name: user?.name || 'Ryan March',
          user_role: 'super_admin'
        }
      }).catch(err => console.log('Failed to create conversation:', err));
    }
  }, [open, sessionId, user]);

  // Fetch proposals when tab changes
  useEffect(() => {
    if (activeTab === 'agents' && open) {
      fetchProposals();
    }
    if (activeTab === 'delegations' && open) {
      fetchDelegations();
    }
  }, [activeTab, open]);

  const fetchProposals = async () => {
    setLoadingProposals(true);
    try {
      const res = await axios.get(`${API}/platform-ai/agent-proposals`);
      setProposals(res.data.proposals || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoadingProposals(false);
    }
  };

  const fetchDelegations = async () => {
    setLoadingDelegations(true);
    try {
      const [delegationsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/prime/delegations?limit=20`),
        axios.get(`${API}/prime/delegation-dashboard`)
      ]);
      setDelegations(delegationsRes.data.delegations || []);
      setDelegationDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error fetching delegations:', error);
    } finally {
      setLoadingDelegations(false);
    }
  };

  // Persist message to conversation
  const persistMessage = async (role, content, tokens = 0) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/prime/conversations/${sessionId}/messages`, null, {
        params: { role, content, tokens }
      });
    } catch (error) {
      console.log('Failed to persist message:', error);
    }
  };

  // Upload file and get URL/analysis
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Upload to chat uploads endpoint
      const res = await axios.post(`${API}/chat/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  };

  // Send message to Prime with memory
  const sendMessage = async (text = inputMessage) => {
    if ((!text.trim() && !selectedFile) || loading) return;
    
    const messageContent = text.trim() || (selectedFile ? `[Analyzing file: ${selectedFile.name}]` : '');
    
    // Check if this is a reply via Prime
    if (replyingTo && messageContent.toLowerCase().startsWith('reply to')) {
      // Extract the actual reply message
      const replyMatch = messageContent.match(/^reply to [^:]+:\s*(.+)$/i);
      if (replyMatch && replyMatch[1]) {
        const replyText = replyMatch[1].trim();
        
        // Add user message to chat
        const userMsg = {
          role: 'user',
          content: messageContent,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMsg]);
        setInputMessage('');
        setLoading(true);
        
        try {
          const result = await handleReplyViaPrime(replyingTo.channel_id, replyText, replyingTo.channel_name);
          
          if (result?.success) {
            const aiMsg = {
              role: 'assistant',
              content: `✅ ${result.message}\n\nYour reply: "${replyText}"`,
              timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, aiMsg]);
          }
        } catch (error) {
          const aiMsg = {
            role: 'assistant',
            content: `❌ Failed to send reply. Please try again or use the Chat page directly.`,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, aiMsg]);
        }
        
        setLoading(false);
        return;
      }
    }
    
    // Check for skip/dismiss commands
    if (messageContent.toLowerCase() === 'skip' || messageContent.toLowerCase() === 'ignore messages' || messageContent.toLowerCase() === 'dismiss') {
      setUnreadDismissed(true);
      setReplyingTo(null);
      
      const userMsg = { role: 'user', content: messageContent, timestamp: new Date().toISOString() };
      const aiMsg = { role: 'assistant', content: "Got it, I'll put those aside for now. What can I help you with?", timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg, aiMsg]);
      setInputMessage('');
      return;
    }
    
    const userMsg = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
      file: selectedFile ? { name: selectedFile.name, type: selectedFile.type } : null
    };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    const currentFile = selectedFile;
    setSelectedFile(null);
    setLoading(true);
    
    // Persist user message
    persistMessage('user', messageContent);
    
    try {
      // Upload file if present
      let fileContext = null;
      if (currentFile) {
        try {
          const uploadResult = await uploadFile(currentFile);
          fileContext = {
            filename: currentFile.name,
            type: currentFile.type,
            url: uploadResult.url,
            analysis: uploadResult.analysis
          };
          toast.success('File uploaded successfully');
        } catch (e) {
          toast.error('Failed to upload file, continuing without it');
        }
      }
      
      // First, retrieve relevant memory
      let memoryContext = [];
      try {
        const memRes = await axios.post(`${API}/prime/memory/retrieve`, {
          query: text,
          context: { market: user?.market, department: user?.department },
          limit: 5
        });
        memoryContext = memRes.data.memories || [];
      } catch (e) {
        console.log('Memory retrieval failed:', e);
      }
      
      // Build message with file context
      let fullMessage = text;
      if (fileContext) {
        fullMessage = `${text}\n\n[Attached file: ${fileContext.filename}${fileContext.analysis ? `\n\nFile Analysis:\n${fileContext.analysis}` : ''}]`;
      }
      
      // Build conversation history from current messages (exclude the one we just added)
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      const response = await axios.post(`${API}/platform-ai/assistant/chat`, {
        message: fullMessage,
        session_id: sessionId,
        user_id: user?.id,
        user_email: user?.email || 'rmarch@rhinoshieldflorida.com',
        department: user?.department || 'admin',
        role: 'super_admin',
        is_prime: true,
        memory_context: memoryContext.map(m => m.content),
        conversation_history: conversationHistory,
        context: {
          page: window.location.pathname,
          attached_file: fileContext
        }
      });
      
      // Check if there's an action result (calendar event, etc.)
      const actionData = response.data.action_data;
      const cardType = response.data.card_type;
      
      const aiMsg = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        tokens: response.data.tokens_used,
        // Include action data for rendering cards
        action: response.data.action,
        cardType: cardType,
        actionData: actionData
      };
      setMessages(prev => [...prev, aiMsg]);
      
      // Handle calendar event action - add to calendarEvents state for card rendering
      if (cardType === 'event_created' && actionData) {
        if (actionData.success) {
          toast.success('✅ Calendar event created successfully!');
          setCalendarEvents(prev => [...prev, {
            ...actionData,
            type: 'success'
          }]);
        } else {
          toast.error(actionData.error || 'Failed to create calendar event');
          setCalendarEvents(prev => [...prev, {
            ...actionData,
            type: 'error'
          }]);
        }
      }
      
      // Handle SMS draft action - store in message, don't duplicate in pendingDrafts
      if (cardType === 'message_draft' && actionData) {
        toast.info('📱 SMS draft ready for your approval');
        // Draft card will be rendered inline with the message via cardType/actionData
      }
      
      // Handle email draft action - store in message, don't duplicate in pendingDrafts
      if (cardType === 'email_draft' && actionData) {
        toast.info('📧 Email draft ready for your approval');
        // Normalize channel for MessagePreviewCard (already has 'email' in actionData.channel)
        // Draft card will be rendered inline with the message via cardType/actionData
      }
      
      // Handle goal created action
      if (cardType === 'goal_created' && actionData) {
        toast.success('🎯 Goal created! Prime will manage this conversation.');
        setActiveGoals(prev => [...prev, actionData]);
      }
      
      // Handle channel message posted
      if (cardType === 'action_result' && actionData?.type === 'CHANNEL_MESSAGE') {
        if (actionData.success) {
          toast.success(`📢 Posted to #${actionData.channel_name || actionData.channel_id} channel!`);
        } else {
          toast.error(actionData.error || 'Failed to post channel message');
        }
      }
      
      // Handle DM draft ready
      if (cardType === 'dm_draft' && actionData) {
        toast.info('💬 Direct message draft ready for your approval');
      }
      
      // Handle DM draft failed
      if (cardType === 'action_result' && actionData?.type === 'DIRECT_MESSAGE') {
        if (!actionData.success) {
          toast.error(actionData.error || 'Failed to create DM draft');
        }
      }
      
      // Persist AI response
      persistMessage('assistant', response.data.response, response.data.tokens_used);
      
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }
    } catch (error) {
      console.error('Prime error:', error);
      const errorMsg = {
        role: 'assistant',
        content: 'I apologize, Ryan. I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Auto-transcribe
        await transcribeAudio(blob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording... Click again to stop');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not access microphone');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Transcribe audio
  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        // For now, use browser's speech recognition as fallback
        // OpenAI Whisper would require file upload endpoint
        const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
        recognition.lang = 'en-US';
        recognition.continuous = false;
        recognition.interimResults = false;
        
        // Since we already recorded, we'll use the Web Speech API for real-time
        // The Whisper integration would need a file upload endpoint
        toast.info('Processing speech...');
        
        // Simulate transcription (in production, this would call the Whisper API)
        setTimeout(() => {
          setTranscribing(false);
          toast.success('Speech processed! Type or speak your message.');
        }, 500);
      };
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Failed to transcribe audio');
      setTranscribing(false);
    }
  };

  // Real-time speech recognition
  const startSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsRecording(true);
      toast.info('Listening... Speak now');
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        setInputMessage(prev => prev + ' ' + finalTranscript.trim());
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech error:', event.error);
      setIsRecording(false);
      if (event.error !== 'no-speech') {
        toast.error(`Speech error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();
    
    // Store reference to stop later
    mediaRecorderRef.current = recognition;
  };

  const stopSpeechRecognition = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.stop) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  // Approve/Reject proposals
  const handleApproveProposal = async (proposalId, mode) => {
    try {
      await axios.post(`${API}/platform-ai/agent-proposals/${proposalId}/approve`, null, {
        params: { approved_by: user?.email || 'rmarch@rhinoshieldflorida.com', mode }
      });
      toast.success(`Agent approved in ${mode} mode!`);
      fetchProposals();
    } catch (error) {
      toast.error('Failed to approve proposal');
    }
  };

  const handleRejectProposal = async (proposalId) => {
    try {
      await axios.post(`${API}/platform-ai/agent-proposals/${proposalId}/reject`, null, {
        params: { rejected_by: user?.email, reason: 'Rejected by Super Admin' }
      });
      toast.success('Proposal rejected');
      fetchProposals();
    } catch (error) {
      toast.error('Failed to reject proposal');
    }
  };

  // Clear chat - now starts a new chat
  const clearChat = () => {
    startNewChat();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] p-0 gap-0 overflow-hidden [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar className="h-14 w-14 border-2 border-amber-500/50">
                <AvatarImage src={primeAvatar} onError={() => setPrimeAvatar(DEFAULT_PRIME_AVATAR)} />
                <AvatarFallback className="bg-gradient-to-br from-amber-500 to-orange-600">
                  <Crown className="h-7 w-7 text-white" />
                </AvatarFallback>
              </Avatar>
              <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {primeProfile?.name || 'Prime'}
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 animate-pulse">
                  Chief-of-Staff Mode
                </Badge>
              </h2>
              <p className="text-slate-400 text-sm flex items-center gap-2">
                <span>Level {primeProfile?.autonomy_level || 2} Bounded Autonomy</span>
                <span className="text-slate-600">•</span>
                <span>Schedule Master + Platform Builder</span>
              </p>
            </div>
          </div>
          
          {/* Header Actions - Better spacing */}
          <div className="flex items-center gap-3">
            {/* Projects Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white gap-2">
                  <FolderOpen className="h-4 w-4" />
                  {selectedProjectId 
                    ? chatProjects.find(p => p.id === selectedProjectId)?.name || 'Project'
                    : 'All Chats'
                  }
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Projects</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowNewProjectDialog(true);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    New
                  </Button>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => {
                    setSelectedProjectId(null);
                    fetchChatHistory();
                  }}
                  className={!selectedProjectId ? 'bg-accent' : ''}
                >
                  <History className="h-4 w-4 mr-2" />
                  All Chats
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {chatProjects.map(project => (
                  <DropdownMenuItem 
                    key={project.id}
                    className={`flex items-center justify-between group ${selectedProjectId === project.id ? 'bg-accent' : ''}`}
                  >
                    <div 
                      className="flex items-center flex-1"
                      onClick={() => {
                        setSelectedProjectId(project.id);
                        fetchChatHistory(project.id);
                      }}
                    >
                      <FolderOpen className="h-4 w-4 mr-2 text-amber-500" />
                      <span className="truncate">{project.name}</span>
                      {project.chat_count > 0 && (
                        <Badge variant="secondary" className="ml-2 h-5 text-xs">
                          {project.chat_count}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Delete this project and all its chats?')) {
                          deleteProject(project.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </DropdownMenuItem>
                ))}
                {chatProjects.length === 0 && (
                  <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                    No projects yet
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Chat History Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white gap-2">
                  <History className="h-4 w-4" />
                  History
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-y-auto">
                <DropdownMenuLabel>Recent Chats</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {loadingChats ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : chatHistory.length > 0 ? (
                  chatHistory.map(chat => (
                    <DropdownMenuItem 
                      key={chat.id}
                      className={`flex items-center justify-between group ${currentChatId === chat.id ? 'bg-accent' : ''}`}
                    >
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => loadChat(chat.id)}
                      >
                        <p className="text-sm font-medium truncate">{chat.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(chat.updated_at).toLocaleDateString()} • {chat.message_count} messages
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteChat(chat.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                    No chat history yet
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* New Chat Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-slate-400 hover:text-white"
              onClick={startNewChat}
            >
              <Plus className="h-4 w-4 mr-1" />
              New Chat
            </Button>
            
            {/* Close Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-slate-400 hover:text-white h-8 w-8"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* New Project Dialog - Using AlertDialog for nested dialog support */}
        <AlertDialog open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <FolderPlus className="h-5 w-5" />
                New Project
              </AlertDialogTitle>
            </AlertDialogHeader>
            <div className="py-4">
              <Input
                placeholder="Project name..."
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newProjectName.trim()) {
                    createProject();
                  }
                }}
                autoFocus
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setNewProjectName('')}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={createProject} disabled={!newProjectName.trim()}>
                Create
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Main Content */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left - Chat Area */}
          <div className="flex-1 flex flex-col min-h-0 border-r">
            {/* Messages - SCROLLABLE AREA */}
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-6 space-y-4 max-w-3xl mx-auto">
                {/* Unread Messages Notification Card */}
                {unreadMessages && !unreadDismissed && unreadMessages.total_unread > 0 && (
                  <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-5 w-5 text-blue-600" />
                          <CardTitle className="text-base text-blue-800 dark:text-blue-200">
                            You have {unreadMessages.total_unread} unread message{unreadMessages.total_unread > 1 ? 's' : ''}
                          </CardTitle>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setUnreadDismissed(true)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Urgent Messages */}
                      {unreadMessages.urgent?.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                            <AlertCircle className="h-3 w-3" />
                            URGENT
                          </div>
                          {unreadMessages.urgent.slice(0, 3).map((msg, idx) => (
                            <div key={idx} className="bg-red-50 dark:bg-red-950/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm text-red-800 dark:text-red-200">
                                  {msg.sender_name} <span className="text-xs text-red-600">({msg.sender_role})</span>
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {msg.channel_type === 'dm' || msg.channel_type === 'direct' ? 'DM' : `#${msg.channel_name}`}
                                </span>
                              </div>
                              <p className="text-sm text-red-700 dark:text-red-300 line-clamp-2">{msg.content}</p>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-red-300 hover:bg-red-100"
                                  onClick={() => {
                                    setReplyingTo({
                                      channel_id: msg.channel_id,
                                      channel_name: msg.channel_name,
                                      sender_name: msg.sender_name,
                                      channel_type: msg.channel_type
                                    });
                                    setInputMessage(`Reply to ${msg.sender_name}: `);
                                  }}
                                >
                                  Reply
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Channel Messages */}
                      {unreadMessages.channels?.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground">Channels</div>
                          {unreadMessages.channels.slice(0, 3).map((ch, idx) => (
                            <div key={idx} className="bg-white dark:bg-slate-900 rounded-lg p-3 border">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">#{ch.channel_name}</span>
                                <Badge variant="secondary" className="text-xs">{ch.unread_count} new</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mb-1">
                                {ch.latest_message.sender_name}: {ch.latest_message.content.slice(0, 80)}...
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs px-2"
                                onClick={() => {
                                  setReplyingTo({
                                    channel_id: ch.channel_id,
                                    channel_name: ch.channel_name,
                                    sender_name: ch.latest_message.sender_name,
                                    channel_type: 'channel'
                                  });
                                  setInputMessage(`Reply to #${ch.channel_name}: `);
                                }}
                              >
                                Reply to #{ch.channel_name}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* DM Messages */}
                      {unreadMessages.dms?.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground">Direct Messages</div>
                          {unreadMessages.dms.slice(0, 3).map((dm, idx) => (
                            <div key={idx} className="bg-white dark:bg-slate-900 rounded-lg p-3 border">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{dm.dm_with || dm.latest_message.sender_name}</span>
                                <Badge variant="secondary" className="text-xs">{dm.unread_count} new</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mb-1">
                                {dm.latest_message.content.slice(0, 80)}...
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs px-2"
                                onClick={() => {
                                  setReplyingTo({
                                    channel_id: dm.channel_id,
                                    channel_name: dm.dm_with || dm.latest_message.sender_name,
                                    sender_name: dm.dm_with || dm.latest_message.sender_name,
                                    channel_type: 'dm'
                                  });
                                  setInputMessage(`Reply to ${dm.dm_with || dm.latest_message.sender_name}: `);
                                }}
                              >
                                Reply
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => handleAcknowledgeMessages(null, true)}
                        >
                          Mark all as read
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs"
                          onClick={() => setUnreadDismissed(true)}
                        >
                          Dismiss for now
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Reply Context Indicator */}
                {replyingTo && (
                  <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
                    <CardContent className="py-2 px-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <MessageSquare className="h-4 w-4 text-amber-600" />
                        <span className="text-amber-800 dark:text-amber-200">
                          Replying to {replyingTo.channel_type === 'channel' ? `#${replyingTo.channel_name}` : replyingTo.sender_name}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setReplyingTo(null);
                          setInputMessage('');
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${
                      msg.isSessionMarker ? 'justify-center' : 
                      msg.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {/* Session marker */}
                    {msg.isSessionMarker ? (
                      <div className="flex items-center gap-2 py-2">
                        <div className="h-px w-12 bg-slate-300 dark:bg-slate-600" />
                        <span className="text-xs text-muted-foreground">{msg.content}</span>
                        <div className="h-px w-12 bg-slate-300 dark:bg-slate-600" />
                      </div>
                    ) : (
                      <>
                        {/* Prime Avatar for assistant messages */}
                        {msg.role === 'assistant' && (
                          <Avatar className={`h-8 w-8 flex-shrink-0 border ${msg.isHistorical ? 'border-slate-300 opacity-70' : 'border-amber-500/30'}`}>
                            <AvatarImage src={primeAvatar} />
                            <AvatarFallback className="bg-gradient-to-br from-amber-500 to-orange-600">
                              <Crown className="h-4 w-4 text-white" />
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className={`max-w-[80%] ${msg.isHistorical ? 'opacity-70' : ''}`}>
                          {msg.role === 'assistant' && (
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-amber-600">
                                Prime {msg.isHistorical && <span className="text-muted-foreground font-normal">(earlier)</span>}
                              </span>
                              {!msg.isHistorical && (
                                <MessageActions
                                  message={msg}
                                  onSaveMemory={handleSaveMemory}
                                  onPinMemory={handlePinMemory}
                                  onMarkIncorrect={handleMarkIncorrect}
                                />
                              )}
                            </div>
                          )}
                          <div
                            className={`rounded-2xl px-4 py-3 ${
                              msg.role === 'user'
                                ? msg.isHistorical 
                                  ? 'bg-blue-400 text-white rounded-br-md'
                                  : 'bg-blue-600 text-white rounded-br-md'
                                : msg.isError
                                ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-bl-md'
                                : msg.isHistorical
                                ? 'bg-slate-50 dark:bg-slate-800/50 rounded-bl-md border border-slate-200 dark:border-slate-700'
                                : 'bg-slate-100 dark:bg-slate-800 rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {/* Show attached file preview if any */}
                            {msg.file && (
                              <div className="mt-2 p-2 bg-white/10 rounded-lg flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                <span className="text-xs">{msg.file.name}</span>
                              </div>
                            )}
                          </div>
                          <div className={`flex items-center gap-2 mt-1 text-xs text-muted-foreground ${msg.role === 'user' ? 'justify-end' : ''}`}>
                            <span>{new Date(msg.timestamp).toLocaleTimeString()}</span>
                            {msg.tokens && <span>• {msg.tokens} tokens</span>}
                          </div>
                          
                          {/* Render Calendar Event Card if this message created an event */}
                          {msg.cardType === 'event_created' && msg.actionData && (
                            <CalendarEventCard
                              event={msg.actionData}
                              type={msg.actionData.success ? 'success' : 'error'}
                              onDelete={msg.actionData.success ? handleDeleteCalendarEvent : undefined}
                            />
                          )}
                          
                          {/* Render Message Draft Card if this message generated a draft */}
                          {(msg.cardType === 'message_draft' || msg.cardType === 'email_draft') && msg.actionData && (
                            <MessagePreviewCard
                              draft={msg.actionData}
                              onSend={(draft) => {
                                handleSendNow(draft);
                                // Clear the card from the message after sending
                                setMessages(prev => prev.map(m => 
                                  m.timestamp === msg.timestamp ? { ...m, cardType: null, actionData: null } : m
                                ));
                              }}
                              onCancel={() => {
                                // Clear from the message
                                setMessages(prev => prev.map(m => 
                                  m.timestamp === msg.timestamp ? { ...m, cardType: null, actionData: null } : m
                                ));
                              }}
                              sending={sendingMessage}
                              user={user}
                        />
                      )}
                      
                      {/* Render DM Draft Card if this message generated a DM draft */}
                      {msg.cardType === 'dm_draft' && msg.actionData && (
                        <Card className="border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 my-2">
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-purple-500" />
                                Direct Message Draft
                              </CardTitle>
                              <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-500 border-purple-500/30">
                                DM
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="flex items-center gap-2 text-sm">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">To: {msg.actionData.recipient_name}</span>
                            </div>
                            <div className="p-3 bg-muted/50 rounded-lg max-h-48 overflow-y-auto">
                              <p className="text-sm whitespace-pre-wrap">{msg.actionData.message_body}</p>
                            </div>
                            <div className="flex items-center gap-2 pt-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  handleSendNow(msg.actionData);
                                  setMessages(prev => prev.map(m => 
                                    m.timestamp === msg.timestamp ? { ...m, cardType: null, actionData: null } : m
                                  ));
                                }}
                                disabled={sendingMessage}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                <Send className="h-3 w-3 mr-1" />
                                {sendingMessage ? 'Sending...' : 'Send DM'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setMessages(prev => prev.map(m => 
                                    m.timestamp === msg.timestamp ? { ...m, cardType: null, actionData: null } : m
                                  ));
                                }}
                              >
                                <X className="h-3 w-3 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Render Goal Created Card if this message created a goal */}
                          {msg.cardType === 'goal_created' && msg.actionData && (
                            <GoalCreatedCard goal={msg.actionData} />
                          )}
                          
                          {/* Render Channel Post Confirmation if this message posted to a channel */}
                          {msg.cardType === 'action_result' && msg.actionData?.type === 'CHANNEL_MESSAGE' && (
                            <div className={`mt-2 p-3 rounded-lg border ${
                              msg.actionData.success 
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' 
                                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            }`}>
                              <div className="flex items-center gap-2">
                                {msg.actionData.success ? (
                                  <>
                                    <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                      <MessageSquare className="h-4 w-4 text-emerald-500" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                                        Posted to #{msg.actionData.channel_name || msg.actionData.channel_id}
                                      </p>
                                      <p className="text-xs text-emerald-600 dark:text-emerald-400">
                                        {msg.actionData.message_preview}
                                      </p>
                                    </div>
                                    <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30">
                                      Sent
                                    </Badge>
                                  </>
                                ) : (
                                  <>
                                    <div className="h-8 w-8 rounded-full bg-red-500/20 flex items-center justify-center">
                                      <AlertTriangle className="h-4 w-4 text-red-500" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-red-700 dark:text-red-300">
                                        Failed to post
                                      </p>
                                      <p className="text-xs text-red-600 dark:text-red-400">
                                        {msg.actionData.error || 'Unknown error'}
                                      </p>
                                    </div>
                                    <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
                                      Error
                                    </Badge>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                      {msg.cardType === 'goal_created' && msg.actionData && (
                        <GoalCreatedCard
                          goal={msg.actionData}
                          onViewGoal={handleViewGoal}
                          onPause={handlePauseGoal}
                          onClose={handleCompleteGoal}
                        />
                      )}
                    </div>
                        {/* User Avatar for user messages */}
                        {msg.role === 'user' && (
                          <Avatar className={`h-8 w-8 flex-shrink-0 ${msg.isHistorical ? 'opacity-70' : ''}`}>
                            <AvatarFallback className="bg-blue-600 text-white text-xs">
                              {user?.name?.charAt(0) || 'R'}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Prime is thinking...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Calendar Events Created */}
                {calendarEvents.map((event, idx) => (
                  <CalendarEventCard
                    key={event.event_id || idx}
                    event={event}
                    type={event.type}
                    onDelete={handleDeleteCalendarEvent}
                  />
                ))}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Quick Actions Bar - FIXED POSITION */}
            <div className="flex-shrink-0 border-t">
              <PrimeQuickActions 
              onInsertAction={(actionBlock) => {
                setInputMessage(prev => prev + (prev ? '\n\n' : '') + actionBlock);
              }}
              onActionComplete={(result) => {
                // Optionally add a message showing the action was completed
                const actionMsg = {
                  role: 'assistant',
                  content: `✅ Action completed successfully!`,
                  timestamp: new Date().toISOString(),
                  actionResult: result
                };
                setMessages(prev => [...prev, actionMsg]);
              }}
            />
            </div>

            {/* Input Area - FIXED TO BOTTOM */}
            <div className="flex-shrink-0 p-4 border-t bg-slate-50 dark:bg-slate-900">
              <div className="max-w-3xl mx-auto">
                <div className="flex gap-3">
                  {/* Voice Button */}
                  <Button
                    variant={isRecording ? "destructive" : "outline"}
                    size="lg"
                    className={`shrink-0 ${isRecording ? 'animate-pulse' : ''}`}
                    onClick={isRecording ? stopSpeechRecognition : startSpeechRecognition}
                  >
                    {isRecording ? (
                      <>
                        <MicOff className="h-5 w-5 mr-2" />
                        Stop
                      </>
                    ) : transcribing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing
                      </>
                    ) : (
                      <>
                        <Mic className="h-5 w-5 mr-2" />
                        Speak
                      </>
                    )}
                  </Button>

                  {/* File Upload Button */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (max 10MB)
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error('File size must be less than 10MB');
                          return;
                        }
                        setSelectedFile(file);
                        toast.success(`File "${file.name}" attached`);
                      }
                    }}
                    accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.json"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                  >
                    <Paperclip className="h-5 w-5" />
                  </Button>

                  {/* Text Input with file preview */}
                  <div className="flex-1 relative">
                    {selectedFile && (
                      <div className="absolute -top-8 left-0 right-0 flex items-center gap-2 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-t-lg border border-b-0 border-blue-200 dark:border-blue-800">
                        {selectedFile.type.startsWith('image/') ? (
                          <ImageIcon className="h-4 w-4 text-blue-500" />
                        ) : (
                          <File className="h-4 w-4 text-blue-500" />
                        )}
                        <span className="text-xs text-blue-600 dark:text-blue-400 truncate flex-1">{selectedFile.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => setSelectedFile(null)}
                        >
                          <XCircle className="h-4 w-4 text-blue-500" />
                        </Button>
                      </div>
                    )}
                    <Input
                      placeholder={selectedFile ? "Add a message about this file..." : "Ask Prime anything... (Press Enter to send)"}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      disabled={loading || transcribing}
                      className={`h-12 text-base ${selectedFile ? 'rounded-t-none' : ''}`}
                    />
                  </div>

                  {/* Send Button */}
                  <Button
                    size="lg"
                    onClick={() => sendMessage()}
                    disabled={(!inputMessage.trim() && !selectedFile) || loading}
                    className="shrink-0 bg-amber-600 hover:bg-amber-700"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  🔱 Prime • Chief-of-Staff Mode • Level {primeProfile?.autonomy_level || 2} Bounded Autonomy
                </p>
              </div>
            </div>
          </div>

          {/* Right - Quick Actions & Proposals */}
          <div className="w-[380px] flex flex-col min-h-0 bg-slate-50 dark:bg-slate-900/50">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="mx-4 mt-4 flex-shrink-0">
                <TabsTrigger value="chat" className="flex-1">Quick Actions</TabsTrigger>
                <TabsTrigger value="delegations" className="flex-1">Delegations</TabsTrigger>
                <TabsTrigger value="agents" className="flex-1">Agents</TabsTrigger>
              </TabsList>

              <TabsContent value="chat" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground uppercase font-medium">Suggested Actions</p>
                  
                  {[
                    { icon: Target, label: "What's my priority today?", color: "text-blue-500" },
                    { icon: FileText, label: "Summarize today's jobs", color: "text-green-500" },
                    { icon: Zap, label: "Create a new AI agent", color: "text-purple-500" },
                    { icon: Clock, label: "Tomorrow's prep brief", color: "text-orange-500" },
                    { icon: Brain, label: "Platform health check", color: "text-cyan-500" },
                  ].map((action, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      className="w-full justify-start h-auto py-3"
                      onClick={() => sendMessage(action.label)}
                    >
                      <action.icon className={`h-4 w-4 mr-3 ${action.color}`} />
                      <span className="text-sm">{action.label}</span>
                    </Button>
                  ))}
                  
                  {/* Active Prime Goals */}
                  {activeGoals.length > 0 && (
                    <>
                      <Separator className="my-4" />
                      <p className="text-xs text-muted-foreground uppercase font-medium flex items-center gap-2">
                        <Target className="h-3 w-3" />
                        Active Goals ({activeGoals.length})
                      </p>
                      <div className="space-y-2 max-h-48 overflow-auto">
                        {activeGoals.slice(0, 3).map((goal) => (
                          <Card key={goal.id} className="border-purple-200 dark:border-purple-800">
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium truncate">{goal.goal_title}</span>
                                <Badge variant="outline" className="text-xs">
                                  {goal.status?.replace('_', ' ')}
                                </Badge>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {goal.recipient_name} • Attempt {goal.current_attempt || 1}/{goal.max_attempts || 3}
                              </div>
                              <div className="flex gap-1 mt-2">
                                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handleViewGoal(goal)}>
                                  View
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handlePauseGoal(goal)}>
                                  {goal.status === 'PAUSED' ? 'Resume' : 'Pause'}
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {activeGoals.length > 3 && (
                          <p className="text-xs text-muted-foreground text-center">
                            +{activeGoals.length - 3} more goals
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  <Separator className="my-4" />

                  <p className="text-xs text-muted-foreground uppercase font-medium">Autonomy Level</p>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">Level 2</span>
                        <Badge className="bg-amber-500/20 text-amber-600">Bounded</Badge>
                      </div>
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle2 className="h-3 w-3" />
                          <span>Create tasks & notes</span>
                        </div>
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle2 className="h-3 w-3" />
                          <span>Draft messages</span>
                        </div>
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          <span>Send messages (approval)</span>
                        </div>
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          <span>Schedule changes (approval)</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="agents" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
                <AgentFactoryPanel 
                  proposals={proposals}
                  loadingProposals={loadingProposals}
                  fetchProposals={fetchProposals}
                  onApprove={handleApproveProposal}
                  onReject={handleRejectProposal}
                />
              </TabsContent>

              {/* Delegations Tab */}
              <TabsContent value="delegations" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
                <div className="space-y-4">
                  {/* Dashboard Summary */}
                  {delegationDashboard && (
                    <div className="grid grid-cols-3 gap-2">
                      <Card className="border-green-200 dark:border-green-900">
                        <CardContent className="p-3 text-center">
                          <p className="text-2xl font-bold text-green-600">{delegationDashboard.summary.completed}</p>
                          <p className="text-xs text-muted-foreground">Completed</p>
                        </CardContent>
                      </Card>
                      <Card className="border-amber-200 dark:border-amber-900">
                        <CardContent className="p-3 text-center">
                          <p className="text-2xl font-bold text-amber-600">{delegationDashboard.summary.in_progress}</p>
                          <p className="text-xs text-muted-foreground">In Progress</p>
                        </CardContent>
                      </Card>
                      <Card className="border-blue-200 dark:border-blue-900">
                        <CardContent className="p-3 text-center">
                          <p className="text-2xl font-bold text-blue-600">{delegationDashboard.summary.pending}</p>
                          <p className="text-xs text-muted-foreground">Pending</p>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground uppercase font-medium">Recent Delegations</p>
                    <Button variant="ghost" size="sm" onClick={fetchDelegations} disabled={loadingDelegations}>
                      <RefreshCw className={`h-3 w-3 ${loadingDelegations ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>

                  {loadingDelegations ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : delegations.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="p-6 text-center">
                        <Bot className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">No delegations yet</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Ask Prime to delegate tasks to your AI workforce
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {delegations.map((delegation) => (
                        <Card key={delegation.id} className={`
                          ${delegation.status === 'completed' ? 'border-green-200 dark:border-green-900' : ''}
                          ${delegation.status === 'in_progress' ? 'border-amber-200 dark:border-amber-900' : ''}
                          ${delegation.status === 'pending' ? 'border-blue-200 dark:border-blue-900' : ''}
                          ${delegation.status === 'failed' ? 'border-red-200 dark:border-red-900' : ''}
                        `}>
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Bot className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium">{delegation.delegated_to_agent_name}</span>
                              </div>
                              <Badge 
                                variant={delegation.status === 'completed' ? 'default' : 'secondary'}
                                className={`text-xs ${
                                  delegation.status === 'completed' ? 'bg-green-500' :
                                  delegation.status === 'in_progress' ? 'bg-amber-500' :
                                  delegation.status === 'failed' ? 'bg-red-500' : ''
                                }`}
                              >
                                {delegation.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                              {delegation.task_description}
                            </p>
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>{delegation.department?.replace('_', ' ')}</span>
                              <span>{new Date(delegation.created_at).toLocaleTimeString()}</span>
                            </div>
                            
                            {/* Show execution result if completed */}
                            {delegation.status === 'completed' && delegation.result && (
                              <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs">
                                <p className="font-medium text-green-700 dark:text-green-400 mb-1">Agent Response:</p>
                                <p className="text-muted-foreground line-clamp-3">{delegation.result}</p>
                                {delegation.execution_time_ms && (
                                  <p className="text-green-600 mt-1">
                                    Completed in {(delegation.execution_time_ms / 1000).toFixed(1)}s
                                  </p>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper function
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

export default PrimeAssistant;
